import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { formatLocalDate, parseServerDateTime } from './timezone';

const TYPE_ICON = {
  workflow_failure: '❌',
  human_approval: '✅',
  copilot_approval: '🤖',
  case_assigned: '📋',
  case_status_changed: '🔄',
  case_commented: '💬',
  case_mentioned: '@',
  case_escalated: '🔺',
  case_sla_warning: '⏳',
  case_sla_breach: '🔴',
  case_watcher_update: '👁',
};

const TYPE_COLOR = {
  workflow_failure: 'var(--error-color)',
  human_approval: 'var(--warning-color)',
  copilot_approval: 'var(--info-color)',
  case_assigned: 'var(--accent-color)',
  case_status_changed: 'var(--info-color)',
  case_commented: 'var(--info-color)',
  case_mentioned: 'var(--accent-color)',
  case_escalated: 'var(--error-color)',
  case_sla_warning: 'var(--warning-color)',
  case_sla_breach: 'var(--error-color)',
  case_watcher_update: 'var(--info-color)',
};

export default function NotificationCenter({ token, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const pollRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/notifications/unread-count`, { headers: authHeaders });
      setUnreadCount(res.data.count || 0);
    } catch {
      // silently ignore
    }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/notifications?limit=50`, { headers: authHeaders });
      setNotifications(res.data || []);
      setUnreadCount((res.data || []).filter(n => !n.is_read).length);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Poll unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchUnreadCount]);

  // Fetch full list when panel opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/notifications/${id}/read`, {}, { headers: authHeaders });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API_BASE_URL}/notifications/read-all`, {}, { headers: authHeaders });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_BASE_URL}/notifications/${id}`, { headers: authHeaders });
      const deleted = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (deleted && !deleted.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch { /* ignore */ }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/notifications`, { headers: authHeaders });
      setNotifications([]);
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const handleClickNotification = (notif) => {
    if (!notif.is_read) {
      handleMarkRead(notif.id);
    }
    if (notif.link_params && onNavigate) {
      try {
        const params = typeof notif.link_params === 'string'
          ? JSON.parse(notif.link_params)
          : notif.link_params;
        onNavigate(params);
      } catch { /* ignore */ }
    }
    setOpen(false);
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = parseServerDateTime(iso);
    if (!d) return '';
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return formatLocalDate(d);
  };

  return (
    <div ref={panelRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(prev => !prev)}
        title="Notifications"
        style={{
          position: 'relative',
          padding: '6px 10px',
          background: open ? 'var(--bg-tertiary)' : 'transparent',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: '16px',
          lineHeight: '1',
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            background: 'var(--error-color)',
            color: '#fff',
            borderRadius: '50%',
            fontSize: '10px',
            fontWeight: 700,
            minWidth: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 3px',
            lineHeight: '1',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: '360px',
          maxHeight: '480px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
              Notifications {unreadCount > 0 && <span style={{ color: 'var(--error-color)' }}>({unreadCount})</span>}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  style={{ background: 'transparent', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '12px', padding: '2px 4px' }}
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '12px', padding: '2px 4px' }}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                Loading…
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                No notifications
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  onClick={() => handleClickNotification(notif)}
                  style={{
                    padding: '10px 16px',
                    borderBottom: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    background: notif.is_read ? 'transparent' : 'rgba(var(--accent-rgb, 99, 102, 241), 0.06)',
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                  onMouseLeave={e => e.currentTarget.style.background = notif.is_read ? 'transparent' : 'rgba(var(--accent-rgb, 99, 102, 241), 0.06)'}
                >
                  <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>
                    {TYPE_ICON[notif.type] || '🔔'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: notif.is_read ? 400 : 600,
                      color: TYPE_COLOR[notif.type] || 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {notif.title}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      marginTop: '2px',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {notif.message}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                      {formatTime(notif.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(notif.id, e)}
                    title="Dismiss"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-tertiary)',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '0 2px',
                      flexShrink: 0,
                      lineHeight: '1',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
